import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import {
  MCP_SCOPE_PERMISSIONS,
  OAuthClientInfo,
  SCALAR_LOGO_URL,
} from './oauthClientInfo';

export interface OAuthConsentScreenProps {
  clientInfo?: OAuthClientInfo | null;
  clientName: string;
  error?: string | null;
  isConnecting: boolean;
  onAllow: () => void;
  onDeny: () => void;
}

const OAuthConsentScreen = ({
  clientInfo,
  clientName,
  error,
  isConnecting,
  onAllow,
  onDeny,
}: OAuthConsentScreenProps) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    bgcolor="background.default">
    <Paper
      elevation={3}
      sx={{ p: 4, maxWidth: 440, width: '100%', mx: 2, textAlign: 'center', borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={3}>
        <img src={SCALAR_LOGO_URL} alt="Scalar" width={40} height={40} />
        <Typography variant="h6" fontWeight={600}>
          &#8596;
        </Typography>
        {clientInfo?.logo_url ? (
          <Box
            component="img"
            src={clientInfo.logo_url}
            alt={clientName}
            width={40}
            height={40}
            sx={{ borderRadius: 1 }}
          />
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: 'action.selected',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography variant="body2" fontWeight={700}>
              {clientName.charAt(0).toUpperCase()}
            </Typography>
          </Box>
        )}
      </Box>

      <Typography variant="h5" fontWeight={700} gutterBottom>
        Connect Scalar to {clientName}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {clientName} is requesting access to your Scalar account. This will allow {clientName} to
        look up your portfolio companies, review valuations, and assist with information requests on
        your behalf.
      </Typography>

      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, textAlign: 'left', bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          display="block"
          mb={1}>
          {clientName.toUpperCase()} WILL BE ABLE TO
        </Typography>
        {MCP_SCOPE_PERMISSIONS.map(item => (
          <Typography key={item} variant="body2" sx={{ mb: 0.5 }}>
            &#10003; &nbsp;{item}
          </Typography>
        ))}
      </Paper>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }} data-testid="oauth-error">
          {error}
        </Typography>
      )}

      <Box display="flex" gap={2}>
        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          onClick={onDeny}
          disabled={isConnecting}
          data-testid="oauth-deny-btn">
          Deny
        </Button>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={onAllow}
          disabled={isConnecting}
          data-testid="oauth-allow-btn"
          startIcon={isConnecting ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {isConnecting ? 'Connecting...' : 'Allow'}
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        You can revoke this access at any time from your account settings.
      </Typography>
    </Paper>
  </Box>
);

export default OAuthConsentScreen;
